import React, { useCallback, useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { ChevronLeft, ListChecks, Send, Download, FileText, AlertCircle, Mail } from "lucide-react";
import { api, API_URL, formatError } from "../lib/api.js";
import { toast } from "sonner";
import { fmtDateTime } from "../lib/format.js";
import CommentThread from "../components/CommentThread.jsx";
import PcpExcelImport from "../components/PcpExcelImport.jsx";
import SomContextBanner from "../components/SomContextBanner.jsx";
import SomFloatingShortcut from "../components/SomFloatingShortcut.jsx";
import ProgressiveStatusControl from "../components/ProgressiveStatusControl.jsx";

const STATUS_COLORS = {
  "Pedido criado": "bg-[#E0E7FF] text-[#1E3A8A]",
  "Pronto para classificar": "bg-[#E0E7FF] text-[#1E3A8A]",
  "Em classificação": "bg-[#FFF3CD] text-[#856404]",
  "Aguarda informação da oficina": "bg-[#FFF3CD] text-[#856404]",
  "Classificado": "bg-[#D1ECF1] text-[#0C5460]",
  "Devolvido ao service advisor": "bg-[#D4EDDA] text-[#155724]",
  "Requote solicitado": "bg-[#FFF3CD] text-[#856404]",
  "Reclassificação em curso": "bg-[#FFF3CD] text-[#856404]",
  "Rejeitado": "bg-[#F8D7DA] text-[#721C24]",
  "Encerrado": "bg-[#E2E8F0] text-[#495057]",
};

const TABS = [
  { key: "overview", label: "Resumo" },
  { key: "comments", label: "Comentários" },
  { key: "audit", label: "Registo de auditoria" },
];

export default function ClassificationRequestDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [pcp, setPcp] = useState(null);
  const [users, setUsers] = useState([]);
  const [v2enums, setV2enums] = useState(null);
  const [tab, setTab] = useState("overview");
  const [notFound, setNotFound] = useState(false);
  const [returnDialog, setReturnDialog] = useState(false);

  const reload = useCallback(async () => {
    try {
      const { data } = await api.get(`/classification-requests/${id}`);
      setPcp(data);
      setNotFound(false);
    } catch (e) {
      if (e?.response?.status === 404) setNotFound(true);
    }
  }, [id]);

  useEffect(() => {
    reload();
    api.get("/users").then((r) => setUsers(r.data));
    api.get("/v2/enums").then((r) => setV2enums(r.data));
  }, [id, reload]);

  if (notFound) {
    return (
      <div className="p-6 text-sm">
        <div className="text-[#DC3545] mb-3">PCP não encontrada.</div>
        <Link to="/classification-requests" className="text-[#1F3864] hover:underline">
          ← Voltar à lista
        </Link>
      </div>
    );
  }
  if (!pcp || !v2enums) return <div className="p-6 text-sm text-[#6C757D]">A carregar…</div>;

  const classifiers = users.filter(
    (u) =>
      !u.deactivated &&
      ["workshop_coordinator", "classifier", "workshop_supervisor", "department_manager"].includes(u.role)
  );

  return (
    <div className="max-w-[1600px] mx-auto">
      {/* Sticky header */}
      <div
        className="sticky top-0 z-30 bg-white border-b border-gray-200 px-4 sm:px-6 py-3 shadow-sm"
        data-testid="pcp-detail-sticky-header"
      >
        <div className="flex items-center gap-2 text-xs text-[#6C757D] mb-1">
          <Link to="/classification-requests" className="flex items-center gap-1 hover:text-[#1F3864]">
            <ChevronLeft className="w-3 h-3" /> Classificação de Peças
          </Link>
          <span>/</span>
          <span className="font-mono" data-testid="pcp-detail-breadcrumb">
            {pcp.number}
          </span>
        </div>
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-3 flex-wrap">
              <h1
                className="font-heading text-xl font-bold text-[#1A1A2E] font-mono"
                data-testid="pcp-detail-number"
              >
                {pcp.number}
              </h1>
              <span
                className={`inline-block text-[10px] font-semibold uppercase tracking-wider px-1.5 py-0.5 rounded-sm ${
                  STATUS_COLORS[pcp.status] || "bg-[#E2E8F0] text-[#495057]"
                }`}
                data-testid="pcp-detail-status-badge"
              >
                {pcp.status}
              </span>
            </div>
            <div className="mt-1.5 flex flex-wrap gap-x-4 gap-y-1 text-xs text-[#6C757D]">
              <span>{pcp.classification_type}</span>
              {pcp.brand && <span>· {pcp.brand}</span>}
              {pcp.branch && <span>· {pcp.branch}</span>}
              {pcp.customer_name && <span>· {pcp.customer_name}</span>}
              {pcp.vehicle_registration && (
                <span className="font-mono">· {pcp.vehicle_registration}</span>
              )}
              {pcp.linked_pec?.number && (
                <span data-testid="pcp-detail-linked-pec">
                  · PEC:{" "}
                  <Link
                    to={`/parts-requests/${pcp.linked_pec.id}`}
                    className="text-[#1F3864] hover:underline font-mono font-semibold"
                  >
                    {pcp.linked_pec.number}
                  </Link>
                </span>
              )}
            </div>
          </div>
        </div>

        {/* Progressive status control */}
        <div className="mt-3">
          <ProgressiveStatusControl
            entity="pcp"
            recordId={id}
            currentStatus={pcp.status}
            onAdvance={async (newStatus, comment) => {
              // Iter 62 · Fix 2 — quando o novo estado é "Devolvido ao service
              // advisor", abrimos um diálogo especializado para recolher o
              // anexo do orçamento Alidata (obrigatório). Só depois disparamos
              // o PATCH final. Para todos os outros estados: fluxo normal.
              if (newStatus === "Devolvido ao service advisor" && !pcp.alidata_quotation_attachment_id) {
                setReturnDialog({ newStatus, comment });
                return;
              }
              await api.patch(`/classification-requests/${id}`, { status: newStatus, comment });
              await reload();
            }}
          />
        </div>

        {/* Tabs */}
        <div className="mt-3 border-b -mb-3 border-gray-200 flex gap-5 overflow-x-auto">
          {TABS.map((tb) => (
            <button
              key={tb.key}
              data-testid={`pcp-detail-tab-${tb.key}`}
              onClick={() => setTab(tb.key)}
              className={`pb-2 text-sm font-medium border-b-2 transition-colors whitespace-nowrap ${
                tab === tb.key
                  ? "border-[#1F3864] text-[#1F3864]"
                  : "border-transparent text-[#6C757D] hover:text-[#1A1A2E]"
              }`}
            >
              {tb.label}
            </button>
          ))}
        </div>
      </div>

      <div className="p-4 sm:p-6">
        {tab === "overview" && (
          <OverviewTab pcp={pcp} classifiers={classifiers} reload={reload} />
        )}
        {tab === "comments" && (
          <div className="max-w-3xl">
            <CommentThread recordType="classification_request" recordId={id} />
          </div>
        )}
        {tab === "audit" && <AuditTab recordType="classification_request" recordId={id} />}
      </div>

      {pcp.service_case_id && (
        <SomFloatingShortcut caseId={pcp.service_case_id} currentModule="pcp" />
      )}

      {returnDialog && (
        <ReturnToAdvisorDialog
          pcp={pcp}
          initial={returnDialog}
          onClose={() => setReturnDialog(false)}
          onDone={async () => {
            setReturnDialog(false);
            await reload();
          }}
        />
      )}
    </div>
  );
}

// -----------------------------------------------------------------------------
// Iter 62 · Fix 2 — diálogo especializado para "Devolvido ao service advisor".
// Recolhe o anexo do orçamento Alidata (PDF/JPG/PNG · obrigatório) antes de
// disparar a mudança de estado. O upload usa `/attachments/upload` e o id
// retornado é enviado no PATCH como `alidata_quotation_attachment_id`.
// -----------------------------------------------------------------------------
function ReturnToAdvisorDialog({ pcp, initial, onClose, onDone }) {
  const [file, setFile] = useState(null);
  const [comment, setComment] = useState(initial?.comment || "Orçamento pronto — devolvido ao service advisor");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  // Iter 62 · Improvement — pré-visualização inline do orçamento antes de
  // submeter. Usa `URL.createObjectURL(file)` para renderizar localmente
  // (nenhum tráfego de rede necessário). O URL é revogado quando o ficheiro
  // muda ou o diálogo desmonta para evitar memory-leaks.
  const previewUrl = React.useMemo(() => (file ? URL.createObjectURL(file) : null), [file]);
  React.useEffect(() => {
    return () => { if (previewUrl) URL.revokeObjectURL(previewUrl); };
  }, [previewUrl]);
  const isPdf = file?.type === "application/pdf" || (file?.name || "").toLowerCase().endsWith(".pdf");
  const isImage = file?.type?.startsWith("image/");

  const submit = async () => {
    setErr("");
    if (!file) {
      setErr("Selecciona o ficheiro do orçamento Alidata antes de submeter.");
      return;
    }
    if (file.size > 10 * 1024 * 1024) {
      setErr("O ficheiro excede 10 MB.");
      return;
    }
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("record_type", "classification_request");
      fd.append("record_id", pcp.id);
      const upload = await api.post("/attachments/upload", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      const attachmentId = upload.data.id;
      await api.patch(`/classification-requests/${pcp.id}`, {
        status: "Devolvido ao service advisor",
        comment: comment.trim() || "Orçamento pronto — devolvido ao service advisor",
        alidata_quotation_attachment_id: attachmentId,
      });
      await onDone();
    } catch (e) {
      setErr(formatError(e?.response?.data?.detail) || "Falha ao devolver ao service advisor.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" onClick={onClose}>
      <div
        className="bg-white rounded-md shadow-xl w-full max-w-2xl max-h-[90vh] flex flex-col"
        onClick={(e) => e.stopPropagation()}
        data-testid="pcp-return-dialog"
      >
        <div className="px-5 py-3 border-b border-gray-200 bg-[#F4F6F8]">
          <h2 className="font-heading text-lg font-bold text-[#1A1A2E]">Devolver ao service advisor</h2>
          <div className="text-xs text-[#6C757D] mt-1">
            O anexo do orçamento Alidata é obrigatório. A SOM avança para <span className="font-mono">Aguarda envio ao cliente</span>.
          </div>
        </div>
        <div className="p-5 space-y-3 overflow-y-auto">
          <label className="block">
            <span className="text-[10px] uppercase tracking-widest text-[#6C757D]">
              Anexar orçamento Alidata (PDF/JPG/PNG · obrigatório) *
            </span>
            <input
              type="file"
              accept=".pdf,.jpg,.jpeg,.png"
              onChange={(e) => setFile(e.target.files?.[0] || null)}
              className="mt-1 block w-full text-sm text-[#1A1A2E]
                file:mr-3 file:py-1.5 file:px-3 file:rounded-sm file:border-0
                file:text-xs file:font-semibold file:bg-[#1F3864] file:text-white
                hover:file:bg-[#142542]"
              data-testid="pcp-return-file-input"
            />
            {file && (
              <div className="mt-1 text-[11px] text-[#6C757D]" data-testid="pcp-return-file-name">
                {file.name} · {_fmtKB(file.size)}
              </div>
            )}
          </label>

          {/* Iter 62 · Improvement — pré-visualização inline. Permite ao SA
              validar visualmente o orçamento antes de submeter. */}
          {previewUrl && (
            <div className="border border-gray-200 rounded-sm overflow-hidden bg-[#F4F6F8]" data-testid="pcp-return-preview">
              <div className="px-3 py-1.5 text-[10px] uppercase tracking-widest text-[#6C757D] bg-white border-b border-gray-200 flex items-center justify-between">
                <span>Pré-visualização</span>
                <a
                  href={previewUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-[#1F3864] hover:underline text-[10px] normal-case tracking-normal"
                  data-testid="pcp-return-preview-open"
                >
                  Abrir em nova aba ↗
                </a>
              </div>
              {isPdf && (
                <embed
                  src={previewUrl}
                  type="application/pdf"
                  className="w-full h-[320px] bg-white"
                  data-testid="pcp-return-preview-pdf"
                />
              )}
              {isImage && (
                <img
                  src={previewUrl}
                  alt="Pré-visualização do orçamento"
                  className="w-full max-h-[320px] object-contain bg-white"
                  data-testid="pcp-return-preview-image"
                />
              )}
              {!isPdf && !isImage && (
                <div className="p-4 text-sm text-[#6C757D]">
                  Este tipo de ficheiro não tem pré-visualização; o upload continua a ser válido.
                </div>
              )}
            </div>
          )}

          <label className="block">
            <span className="text-[10px] uppercase tracking-widest text-[#6C757D]">Comentário</span>
            <textarea
              value={comment}
              onChange={(e) => setComment(e.target.value)}
              className="mt-1 w-full px-2 py-1.5 border border-gray-300 rounded-sm text-sm h-16 focus:outline-none focus:ring-2 focus:ring-[#1F3864]/30"
              data-testid="pcp-return-comment"
            />
          </label>
          {err && (
            <div
              className="px-3 py-2 bg-[#F8D7DA] text-[#721C24] border border-[#DC3545]/30 rounded-sm text-sm"
              data-testid="pcp-return-error"
            >
              {err}
            </div>
          )}
        </div>
        <div className="px-5 py-3 border-t border-gray-200 flex justify-end gap-2">
          <button
            onClick={onClose}
            disabled={busy}
            className="px-4 py-2 text-sm border border-gray-300 rounded-sm hover:bg-[#F4F6F8] disabled:opacity-60"
            data-testid="pcp-return-cancel"
          >
            Cancelar
          </button>
          <button
            onClick={submit}
            disabled={busy}
            className="px-4 py-2 text-sm bg-[#1F3864] hover:bg-[#142542] text-white rounded-sm disabled:opacity-60"
            data-testid="pcp-return-submit"
          >
            {busy ? "A enviar…" : "Devolver ao service advisor"}
          </button>
        </div>
      </div>
    </div>
  );
}

function OverviewTab({ pcp, classifiers, reload }) {
  const [form, setForm] = useState({
    classifier_id: pcp.classifier_id || "",
    technical_findings: pcp.technical_findings || "",
    recommendation: pcp.recommendation || "",
    alidata_quotation_number: pcp.alidata_quotation_number || "",
  });
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);

  const dirty =
    form.classifier_id !== (pcp.classifier_id || "") ||
    form.technical_findings !== (pcp.technical_findings || "") ||
    form.recommendation !== (pcp.recommendation || "") ||
    form.alidata_quotation_number !== (pcp.alidata_quotation_number || "");

  const save = async () => {
    setErr("");
    setBusy(true);
    try {
      const payload = {};
      if (form.classifier_id !== (pcp.classifier_id || ""))
        payload.classifier_id = form.classifier_id || null;
      if (form.technical_findings !== (pcp.technical_findings || ""))
        payload.technical_findings = form.technical_findings;
      if (form.recommendation !== (pcp.recommendation || ""))
        payload.recommendation = form.recommendation;
      if (form.alidata_quotation_number !== (pcp.alidata_quotation_number || ""))
        payload.alidata_quotation_number = form.alidata_quotation_number || null;
      await api.patch(`/classification-requests/${pcp.id}`, payload);
      await reload();
    } catch (e) {
      setErr(formatError(e?.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  const parts = pcp.classified_parts || [];

  return (
    <div className="space-y-5">
      {pcp.service_case_id && (
        <SomContextBanner caseId={pcp.service_case_id} testid="pcp-detail-som-context" />
      )}

      <section className="grid grid-cols-2 sm:grid-cols-4 gap-3 bg-white border border-gray-200 rounded-sm p-3">
        <Info label="Tipo" value={pcp.classification_type} testid="pcp-detail-info-type" />
        <Info label="Aberto em" value={fmtDateTime(pcp.opened_at)} />
        <Info label="Marca · Filial" value={`${pcp.brand || "—"} · ${pcp.branch || "—"}`} />
        <Info
          label="Classificador"
          value={pcp.classifier_name || "(sem atribuição)"}
          testid="pcp-detail-info-classifier"
        />
      </section>

      <section>
        <div className="text-[10px] uppercase tracking-widest text-[#6C757D] mb-1">
          Descrição do advisor
        </div>
        <div className="bg-[#F4F6F8] rounded-sm p-3 text-sm" data-testid="pcp-detail-description">
          {pcp.description || "—"}
        </div>
      </section>

      {/* Iter 62 · Improvement — pré-visualização do orçamento Alidata quando
          o PCP está anexado. Usa o endpoint de download inline (cookies são
          enviados automaticamente, mesma origem). */}
      {pcp.alidata_quotation_attachment_id && (
        <AlidataQuotationPreview pcp={pcp} reload={reload} />
      )}

      {/* Classified parts + Excel import */}
      <section>
        <div className="flex flex-wrap items-center justify-between gap-2 mb-2">
          <h3 className="font-heading text-sm font-bold text-[#1A1A2E] flex items-center gap-1.5">
            <ListChecks className="w-4 h-4 text-[#1F3864]" />
            Peças classificadas
            <span className="text-xs font-normal text-[#6C757D]" data-testid="pcp-detail-parts-count">
              ({parts.length})
            </span>
          </h3>
          <PcpExcelImport pcp={pcp} onImported={reload} />
        </div>
        <div
          className="bg-white border border-gray-200 rounded-sm overflow-hidden"
          data-testid="pcp-detail-parts-table"
        >
          <table className="w-full text-sm">
            <thead className="bg-[#F4F6F8] text-[10px] uppercase tracking-wider text-[#6C757D]">
              <tr>
                <th className="px-3 py-2 text-left">Nº da peça</th>
                <th className="px-3 py-2 text-left">Descrição</th>
                <th className="px-3 py-2 text-right">Qtd</th>
                <th className="px-3 py-2 text-left">Marca</th>
                <th className="px-3 py-2 text-left">Fonte</th>
                <th className="px-3 py-2 text-left">Notas</th>
              </tr>
            </thead>
            <tbody>
              {parts.length === 0 ? (
                <tr>
                  <td colSpan="6" className="px-3 py-4 text-center text-xs text-[#6C757D]">
                    Sem peças classificadas ainda. Descarregue o template Excel e importe as linhas.
                  </td>
                </tr>
              ) : (
                parts.map((p, i) => (
                  <tr
                    key={p.id || i}
                    className="border-t border-gray-100"
                    data-testid={`pcp-detail-part-row-${i}`}
                  >
                    <td className="px-3 py-2 font-mono text-xs text-[#1F3864]">{p.part_number}</td>
                    <td className="px-3 py-2 text-xs">{p.description}</td>
                    <td className="px-3 py-2 text-right font-mono text-xs">{p.quantity}</td>
                    <td className="px-3 py-2 text-xs">{p.brand}</td>
                    <td className="px-3 py-2 text-xs">{p.source}</td>
                    <td className="px-3 py-2 text-xs text-[#6C757D]">{p.notes || "—"}</td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </section>

      {/* Editable classifier / findings / recommendation */}
      <section className="space-y-3">
        <Field label="Classificador">
          <select
            value={form.classifier_id}
            onChange={(e) => setForm({ ...form, classifier_id: e.target.value })}
            className={inputCls}
            data-testid="pcp-detail-classifier"
          >
            <option value="">(sem atribuição)</option>
            {classifiers.map((u) => (
              <option key={u.id} value={u.id}>
                {`${u.name} · ${u.role.replaceAll("_", " ")}`}
              </option>
            ))}
          </select>
        </Field>
        <Field label="Apuramentos técnicos">
          <textarea
            value={form.technical_findings}
            onChange={(e) => setForm({ ...form, technical_findings: e.target.value })}
            rows={4}
            className={inputCls}
            placeholder="Descreve o diagnóstico técnico, sintomas observados e causa identificada..."
            data-testid="pcp-detail-findings"
          />
        </Field>
        <Field label="Recomendação">
          <textarea
            value={form.recommendation}
            onChange={(e) => setForm({ ...form, recommendation: e.target.value })}
            rows={3}
            className={inputCls}
            data-testid="pcp-detail-recommendation"
          />
        </Field>
        <Field label="Nº de cotação Alidata">
          <input
            value={form.alidata_quotation_number}
            onChange={(e) =>
              setForm({ ...form, alidata_quotation_number: e.target.value })
            }
            className={`${inputCls} font-mono`}
            placeholder="OA…"
            data-testid="pcp-detail-alidata"
          />
        </Field>

        {err && (
          <div
            className="px-3 py-2 bg-[#F8D7DA] text-[#721C24] border border-[#DC3545]/30 rounded-sm text-sm"
            data-testid="pcp-detail-save-error"
          >
            {err}
          </div>
        )}

        <div className="flex justify-end">
          <button
            onClick={save}
            disabled={busy || !dirty}
            className="px-4 py-2 text-sm bg-[#1F3864] hover:bg-[#142542] text-white rounded-sm flex items-center gap-1 disabled:opacity-60 disabled:cursor-not-allowed"
            data-testid="pcp-detail-save-btn"
          >
            <Send className="w-3 h-3" /> {busy ? "A gravar…" : "Gravar alterações"}
          </button>
        </div>
      </section>
    </div>
  );
}

function AuditTab({ recordType, recordId }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    api
      .get(`/audit-log?record_type=${recordType}&record_id=${recordId}`)
      .then((r) => setRows(r.data))
      .finally(() => setLoading(false));
  }, [recordType, recordId]);

  if (loading) return <div className="text-xs text-[#6C757D]">A carregar…</div>;
  if (!rows.length) return <div className="text-xs text-[#6C757D]">Sem registos.</div>;
  return (
    <div className="bg-white border border-gray-200 rounded-sm overflow-hidden" data-testid="pcp-detail-audit">
      <table className="w-full text-sm">
        <thead className="bg-[#F4F6F8] text-[10px] uppercase tracking-wider text-[#6C757D]">
          <tr>
            <th className="px-3 py-2 text-left">Timestamp</th>
            <th className="px-3 py-2 text-left">Utilizador</th>
            <th className="px-3 py-2 text-left">Ação</th>
            <th className="px-3 py-2 text-left">Comentário</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r, i) => (
            <tr key={i} className="border-t border-gray-100">
              <td className="px-3 py-2 text-xs text-[#6C757D] whitespace-nowrap">
                {fmtDateTime(r.timestamp)}
              </td>
              <td className="px-3 py-2 text-xs">{r.user_name || r.user_email || "—"}</td>
              <td className="px-3 py-2 text-xs font-semibold text-[#1F3864]">{r.action}</td>
              <td className="px-3 py-2 text-xs">{r.comment || "—"}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <label className="block">
      <span className="text-[10px] uppercase tracking-widest text-[#6C757D]">{label}</span>
      {children}
    </label>
  );
}

function Info({ label, value, testid }) {
  return (
    <div>
      <div className="text-[10px] uppercase tracking-widest text-[#6C757D]">{label}</div>
      <div className="text-sm text-[#1A1A2E]" data-testid={testid}>
        {value || "—"}
      </div>
    </div>
  );
}

const inputCls =
  "w-full mt-0.5 px-2 py-1.5 border border-gray-300 rounded-sm text-sm focus:outline-none focus:ring-2 focus:ring-[#1F3864]/30";

function _fmtKB(bytes) {
  if (!bytes || bytes <= 0) return "0 B";
  if (bytes < 1024) return `${bytes} B`;
  return `${(bytes / 1024).toFixed(bytes < 10240 ? 1 : 0)} KB`;
}

// -----------------------------------------------------------------------------
// Iter 62 · Improvement — pré-visualização inline do orçamento Alidata anexado.
// Busca os metadados do anexo, cria um blob URL autenticado (para PDFs — o
// endpoint /attachments/{id}/download exige auth por cookie/header) e
// renderiza `<embed>` (PDF) ou `<img>` (JPG/PNG). Fallback textual com link
// para descarregar caso o navegador não suporte a pré-visualização.
// -----------------------------------------------------------------------------
function AlidataQuotationPreview({ pcp, reload }) {
  const attachmentId = pcp.alidata_quotation_attachment_id;
  const [meta, setMeta] = useState(null);
  const [blobUrl, setBlobUrl] = useState(null);
  const [err, setErr] = useState("");
  const [sendDialog, setSendDialog] = useState(false);

  useEffect(() => {
    let cancelled = false;
    let createdUrl = null;
    (async () => {
      try {
        const res = await api.get(`/attachments/${attachmentId}/download`, {
          responseType: "blob",
        });
        if (cancelled) return;
        const blob = res.data;
        createdUrl = URL.createObjectURL(blob);
        setBlobUrl(createdUrl);
        const cd = res.headers?.["content-disposition"] || "";
        const m = cd.match(/filename="?([^";]+)"?/i);
        setMeta({
          filename: m ? m[1] : "orcamento.pdf",
          content_type: blob.type || "application/octet-stream",
          size: blob.size || 0,
        });
      } catch (e) {
        if (!cancelled) setErr(formatError(e?.response?.data?.detail) || "Não foi possível carregar a pré-visualização.");
      }
    })();
    return () => {
      cancelled = true;
      if (createdUrl) URL.revokeObjectURL(createdUrl);
    };
  }, [attachmentId]);

  const isPdf = (meta?.content_type || "").includes("pdf");
  const isImage = (meta?.content_type || "").startsWith("image/");
  const downloadHref = `${API_URL}/attachments/${attachmentId}/download`;

  return (
    <section data-testid="pcp-alidata-quotation-preview">
      <div className="flex flex-wrap items-center justify-between gap-2 mb-2">
        <h3 className="font-heading text-sm font-bold text-[#1A1A2E] flex items-center gap-1.5">
          <FileText className="w-4 h-4 text-[#1F3864]" />
          Orçamento Alidata anexado
          {meta && (
            <span className="text-xs font-normal text-[#6C757D]" data-testid="pcp-alidata-quotation-meta">
              · {meta.filename} · {_fmtKB(meta.size)}
            </span>
          )}
        </h3>
        <div className="flex items-center gap-2">
          <a
            href={downloadHref}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-1 px-2 py-1 text-[11px] rounded-sm border border-[#1F3864]/30 text-[#1F3864] hover:bg-[#1F3864] hover:text-white transition"
            data-testid="pcp-alidata-quotation-download"
          >
            <Download className="w-3 h-3" /> Descarregar
          </a>
          <button
            onClick={() => setSendDialog(true)}
            className="inline-flex items-center gap-1 px-2 py-1 text-[11px] rounded-sm bg-[#1F3864] hover:bg-[#142542] text-white transition"
            data-testid="pcp-quote-email-open"
            title="Enviar o orçamento ao cliente por email"
          >
            <Mail className="w-3 h-3" /> Enviar por email
          </button>
        </div>
      </div>
      <div className="bg-white border border-gray-200 rounded-sm overflow-hidden">
        {err && (
          <div
            className="p-4 flex items-start gap-2 text-sm text-[#721C24] bg-[#F8D7DA]"
            data-testid="pcp-alidata-quotation-error"
          >
            <AlertCircle className="w-4 h-4 mt-0.5 flex-shrink-0" />
            <div>{err}</div>
          </div>
        )}
        {!err && !blobUrl && (
          <div className="p-6 text-center text-sm text-[#6C757D]" data-testid="pcp-alidata-quotation-loading">
            A carregar pré-visualização…
          </div>
        )}
        {blobUrl && isPdf && (
          <embed
            src={blobUrl}
            type="application/pdf"
            className="w-full h-[480px] bg-white"
            data-testid="pcp-alidata-quotation-pdf"
          />
        )}
        {blobUrl && isImage && (
          <img
            src={blobUrl}
            alt="Orçamento Alidata"
            className="w-full max-h-[480px] object-contain bg-white"
            data-testid="pcp-alidata-quotation-image"
          />
        )}
        {blobUrl && !isPdf && !isImage && (
          <div className="p-4 text-sm text-[#6C757D]" data-testid="pcp-alidata-quotation-unsupported">
            Este tipo de ficheiro não suporta pré-visualização inline. Usa o botão <b>Descarregar</b> para abrir.
          </div>
        )}
      </div>
      {sendDialog && (
        <SendQuotationEmailDialog
          pcp={pcp}
          onClose={() => setSendDialog(false)}
          onSent={async () => {
            setSendDialog(false);
            await reload();
          }}
        />
      )}
    </section>
  );
}

// -----------------------------------------------------------------------------
// Iter 62 · Enhancement — enviar orçamento por email ao cliente
// Diálogo com destinatário, subject, corpo editável e opção de auto-completar
// a tarefa "Enviar orçamento ao cliente". Chama POST
// /classification-requests/{id}/send-quotation-email (backend anexa o PDF).
// -----------------------------------------------------------------------------
function SendQuotationEmailDialog({ pcp, onClose, onSent }) {
  const [caseInfo, setCaseInfo] = useState(null);
  const [to, setTo] = useState("");
  const [subject, setSubject] = useState("");
  const [body, setBody] = useState("");
  const [completeTask, setCompleteTask] = useState(true);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  useEffect(() => {
    (async () => {
      if (!pcp.service_case_id) return;
      try {
        const { data } = await api.get(`/service-cases/${pcp.service_case_id}`);
        setCaseInfo(data);
        setTo((data.customer_email || "").trim());
        setSubject(`Orçamento · SOM ${data.number || ""} · Ronil LDA`);
        setBody(
          [
            "Bom dia,",
            "",
            `Segue em anexo o orçamento referente à sua viatura ${data.vehicle_registration || "—"}.`,
            "Fico ao dispor para qualquer esclarecimento.",
            "",
            "Cumprimentos,",
          ].join("\n"),
        );
      } catch (e) {
        setErr(formatError(e?.response?.data?.detail) || "Não foi possível carregar os dados do cliente.");
      }
    })();
  }, [pcp.service_case_id]);

  const submit = async () => {
    setErr("");
    const cleanTo = (to || "").trim();
    if (!cleanTo || !cleanTo.includes("@")) {
      setErr("Indica um email de destino válido para o cliente.");
      return;
    }
    setBusy(true);
    try {
      const { data } = await api.post(`/classification-requests/${pcp.id}/send-quotation-email`, {
        to: cleanTo,
        subject: subject.trim() || undefined,
        body: body || undefined,
        complete_task: completeTask,
      });
      toast.success(
        data.task_completed_id
          ? `Orçamento enviado para ${data.recipient} e tarefa concluída.`
          : `Orçamento enviado para ${data.recipient}.`,
      );
      await onSent();
    } catch (e) {
      const detail = e?.response?.data?.detail;
      setErr(
        typeof detail === "string" && detail.trim()
          ? detail
          : "Falha ao enviar o orçamento. Verifica a configuração Resend e o email do cliente.",
      );
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" onClick={onClose}>
      <div
        className="bg-white rounded-md shadow-xl w-full max-w-xl max-h-[90vh] flex flex-col"
        onClick={(e) => e.stopPropagation()}
        data-testid="pcp-quote-email-dialog"
      >
        <div className="px-5 py-3 border-b border-gray-200 bg-[#F4F6F8]">
          <h2 className="font-heading text-lg font-bold text-[#1A1A2E] flex items-center gap-2">
            <Mail className="w-4 h-4 text-[#1F3864]" /> Enviar orçamento por email
          </h2>
          <div className="text-xs text-[#6C757D] mt-1">
            O PDF anexo é enviado automaticamente. O contacto é registado na SOM e a tarefa &quot;Enviar orçamento ao cliente&quot; é fechada.
          </div>
        </div>
        <div className="p-5 space-y-3 overflow-y-auto">
          <Field label="Para (email do cliente) *">
            <input
              type="email"
              value={to}
              onChange={(e) => setTo(e.target.value)}
              placeholder="cliente@example.com"
              className={inputCls}
              data-testid="pcp-quote-email-to"
            />
            {caseInfo && (
              <div className="mt-1 text-[11px] text-[#6C757D]">
                Cliente: <b>{caseInfo.customer_name || "—"}</b>
                {caseInfo.customer_phone ? ` · ${caseInfo.customer_phone}` : ""}
              </div>
            )}
          </Field>
          <Field label="Assunto">
            <input
              type="text"
              value={subject}
              onChange={(e) => setSubject(e.target.value)}
              className={inputCls}
              data-testid="pcp-quote-email-subject"
            />
          </Field>
          <Field label="Corpo do email">
            <textarea
              value={body}
              onChange={(e) => setBody(e.target.value)}
              className="w-full mt-0.5 px-2 py-1.5 border border-gray-300 rounded-sm text-sm h-40 focus:outline-none focus:ring-2 focus:ring-[#1F3864]/30"
              data-testid="pcp-quote-email-body"
            />
          </Field>
          <label className="flex items-center gap-2 text-xs text-[#495057]">
            <input
              type="checkbox"
              checked={completeTask}
              onChange={(e) => setCompleteTask(e.target.checked)}
              data-testid="pcp-quote-email-complete-task"
            />
            Fechar automaticamente a tarefa &quot;Enviar orçamento ao cliente&quot; após envio bem-sucedido.
          </label>
          {err && (
            <div
              className="px-3 py-2 bg-[#F8D7DA] text-[#721C24] border border-[#DC3545]/30 rounded-sm text-sm"
              data-testid="pcp-quote-email-error"
            >
              {err}
            </div>
          )}
        </div>
        <div className="px-5 py-3 border-t border-gray-200 flex justify-end gap-2">
          <button
            onClick={onClose}
            disabled={busy}
            className="px-4 py-2 text-sm border border-gray-300 rounded-sm hover:bg-[#F4F6F8] disabled:opacity-60"
            data-testid="pcp-quote-email-cancel"
          >
            Cancelar
          </button>
          <button
            onClick={submit}
            disabled={busy}
            className="px-4 py-2 text-sm bg-[#1F3864] hover:bg-[#142542] text-white rounded-sm inline-flex items-center gap-1 disabled:opacity-60"
            data-testid="pcp-quote-email-submit"
          >
            <Send className="w-3 h-3" /> {busy ? "A enviar…" : "Enviar orçamento"}
          </button>
        </div>
      </div>
    </div>
  );
}
